import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BRANDING } from "@/config/branding";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Home, ArrowLeftRight, PiggyBank, Inbox, Compass, TrendingUp, FileText,
  Building2, ShieldCheck, CalendarDays, Landmark, Users, Settings, LogOut, ShieldAlert, MailWarning, X,
  CreditCard, LifeBuoy, UserCircle,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Home", icon: Home, module: null, id: "home" },
  { to: "/cash-flow", label: "Cash Flow", icon: ArrowLeftRight, module: "expenses", id: "cash-flow" },
  { to: "/savings", label: "Savings", icon: PiggyBank, module: "savings", id: "savings" },
  { to: "/inbox", label: "Financial Inbox", icon: Inbox, module: "documents", id: "inbox" },
  { to: "/insights", label: "Insights", icon: Compass, module: "ai_coach", id: "insights" },
  { to: "/portfolio", label: "Portfolio", icon: TrendingUp, module: "portfolio", id: "portfolio" },
  { to: "/suppliers", label: "Suppliers", icon: FileText, module: "supplier_invoices", id: "suppliers" },
  { to: "/properties", label: "Properties", icon: Building2, module: "properties", id: "properties" },
  { to: "/insurance", label: "Insurance", icon: ShieldCheck, module: "insurance", id: "insurance" },
  { to: "/bookings", label: "Bookings", icon: CalendarDays, module: "bookings", id: "bookings" },
  { to: "/treasurer", label: "Treasurer", icon: Landmark, module: "treasurer", id: "treasurer" },
  { to: "/members", label: "Members", icon: Users, module: "member_management", id: "members" },
];

function VerifyBanner() {
  const { user, resendVerification } = useAuth();
  const [hidden, setHidden] = useState(false);
  if (!user || user.email_verified || hidden) return null;
  const resend = async () => {
    try {
      const d = await resendVerification();
      toast.success("Verification link created", { description: d.verify_link ? "Check your inbox to confirm your email." : undefined });
    } catch { toast.error("Could not resend right now"); }
  };
  return (
    <div data-testid="verify-banner" className="mb-6 flex items-center gap-3 bg-[#C6A45C]/10 border border-[#C6A45C]/30 rounded-xl px-4 py-3">
      <MailWarning size={18} className="text-[#8a6d2f] shrink-0" />
      <span className="text-sm text-[#6b5a32] flex-1">Please confirm your email to fully secure your account.</span>
      <button data-testid="verify-resend" onClick={resend} className="text-sm gold-text font-medium hover:underline">Resend link</button>
      <button data-testid="verify-dismiss" onClick={() => setHidden(true)} className="text-[#8A7F70] hover:text-[#2C2A28]"><X size={16} /></button>
    </div>
  );
}

export default function Layout() {
  const { user, logout, hasModule, entitlements } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/auth"); };

  const visible = NAV.filter((n) => hasModule(n.module));

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all duration-300 ${
      isActive ? "bg-[#C6A45C]/15 text-[#8a6d2f] border border-[#C6A45C]/30"
               : "text-[#6b6256] hover:bg-white/70 border border-transparent"}`;

  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-64 flex-col fixed h-screen bg-[#FBF7F1]/80 backdrop-blur-md border-r border-[#EAE0D2]">
        <div className="px-7 py-7 border-b border-[#EAE0D2]">
          <div className="font-display text-3xl tracking-[0.1em] gold-text">{BRANDING.name}</div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#A99F90] mt-1">{BRANDING.tagline}</div>
        </div>
        <nav className="flex-1 px-4 py-5 space-y-1 overflow-y-auto">
          {visible.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.id}`} end={n.to === "/"} className={linkCls}>
              <n.icon size={18} strokeWidth={1.5} /><span>{n.label}</span>
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-platform-admin" className={({ isActive }) => `${linkCls({ isActive })} mt-2`}>
              <ShieldAlert size={18} strokeWidth={1.5} /><span>Platform Admin</span>
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-[#EAE0D2]">
          <NavLink to="/subscription" data-testid="nav-subscription" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#6b6256] hover:bg-white/70 transition-all">
            <CreditCard size={18} strokeWidth={1.5} /> Subscription
          </NavLink>
          <NavLink to="/support" data-testid="nav-support" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#6b6256] hover:bg-white/70 transition-all">
            <LifeBuoy size={18} strokeWidth={1.5} /> Support
          </NavLink>
          <NavLink to="/settings" data-testid="nav-settings" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#6b6256] hover:bg-white/70 transition-all mb-2">
            <Settings size={18} strokeWidth={1.5} /> Settings
          </NavLink>
          <div className="flex items-center justify-between px-2">
            <div className="min-w-0">
              <div className="text-sm truncate text-[#2C2A28]">{user?.name}</div>
              <div className="text-[11px] gold-text truncate">{entitlements?.tier_name}</div>
            </div>
            <button data-testid="logout-btn" onClick={doLogout} className="text-[#8A7F70] hover:text-[#C99FA0] transition-colors p-2" title="Sign out">
              <LogOut size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#FBF7F1]/95 backdrop-blur border-b border-[#EAE0D2] flex items-center gap-1 px-2 py-2 overflow-x-auto">
        <span className="font-display text-lg gold-text px-2">{BRANDING.name}</span>
        {visible.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`mnav-${n.id}`}
            className={({ isActive }) => `p-2 rounded-lg ${isActive ? "text-[#8a6d2f] bg-[#C6A45C]/15" : "text-[#8A7F70]"}`}>
            <n.icon size={18} strokeWidth={1.5} />
          </NavLink>
        ))}
        <NavLink to="/settings" className="p-2 text-[#8A7F70]"><Settings size={18} strokeWidth={1.5} /></NavLink>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="mobile-account-menu" className="p-2 text-[#8A7F70]"><UserCircle size={20} strokeWidth={1.5} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#FBF7F1] border-[#EAE0D2] text-[#2C2A28]">
            <DropdownMenuLabel className="text-xs text-[#8A7F70] font-normal truncate max-w-[200px]">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#EAE0D2]" />
            <DropdownMenuItem data-testid="m-acct-settings" onClick={() => navigate("/settings")} className="gap-2 cursor-pointer"><Settings size={16} /> Profile &amp; Settings</DropdownMenuItem>
            <DropdownMenuItem data-testid="m-acct-subscription" onClick={() => navigate("/subscription")} className="gap-2 cursor-pointer"><CreditCard size={16} /> Subscription</DropdownMenuItem>
            <DropdownMenuItem data-testid="m-acct-support" onClick={() => navigate("/support")} className="gap-2 cursor-pointer"><LifeBuoy size={16} /> Support</DropdownMenuItem>
            {user?.role === "admin" && (
              <DropdownMenuItem data-testid="m-acct-admin" onClick={() => navigate("/admin")} className="gap-2 cursor-pointer"><ShieldAlert size={16} /> Platform Admin</DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-[#EAE0D2]" />
            <DropdownMenuItem data-testid="m-acct-logout" onClick={doLogout} className="gap-2 cursor-pointer text-[#b06b6b]"><LogOut size={16} /> Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <main className="flex-1 md:ml-64 px-5 md:px-10 py-8 md:py-10 mt-14 md:mt-0 w-full max-w-[1500px]">
        <VerifyBanner />
        <Outlet />
      </main>
    </div>
  );
}
